/* global indexUrl, exportBudgetCodesUrl, deleteManyBudgetCodesUrl, csrfToken, editUrl, bulkAssignGlCodeUrl, bulkAssignGstUrl, updateGstUrl, budgetId, taxLineRevenueToggleUrl, taxLineExpenseToggleUrl, taxLineBulkToggleUrl */
import React, { Fragment, useEffect, useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import Select, { components as SelectComponents } from 'react-select';

import { formatAndRoundMoney, formatFigure } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import ToggleInput from 'CommonComponents/ToggleInput';
import Tooltip from 'CommonComponents/Tooltip';

import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import InfoCircleIcon from 'Assets/icons/circle-info.svg';
import ExportIcon from 'Assets/icons/export.svg';
import EyeIcon from 'Assets/icons/eye.svg';

const STORAGE_KEY_PREFIX = 'budget_lines_collapse_';
// Base column count without GST: Description, Expenditure type, Value, Cash/VIK, GL Code, Actions = 6
// When GST is enabled, add 1 for the GST/VAT column. Tax line columns are appended dynamically.
const COL_SPAN_BASE = 6;

function getStorageKey() {
    return STORAGE_KEY_PREFIX + (typeof budgetId !== 'undefined' ? budgetId : 'default');
}

function loadFullCollapseState() {
    const empty = {
        groups: {},
        templates: {},
        projects: {},
        projectTemplates: {},
        packs: {},
        workforce: {},
        revenues: { revenues: true, sponsors: true, ticketing: true },
    };
    try {
        const raw = localStorage.getItem(getStorageKey());
        if (!raw) return empty;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return empty;

        const hasNewShape =
            'groups' in parsed || 'templates' in parsed || 'projects' in parsed || 'projectTemplates' in parsed || 'packs' in parsed || 'workforce' in parsed;
        if (hasNewShape) {
            return {
                groups: parsed.groups ?? {},
                templates: parsed.templates ?? {},
                projects: parsed.projects ?? {},
                projectTemplates: parsed.projectTemplates ?? {},
                packs: parsed.packs ?? {},
                workforce: parsed.workforce ?? {},
                revenues: parsed.revenues ?? empty.revenues,
            };
        }

        const values = Object.values(parsed);
        const isFlatGroups = values.length === 0 || values.every((v) => typeof v === 'boolean');
        if (isFlatGroups) {
            return { ...empty, groups: parsed };
        }

        return empty;
    } catch {
        return empty;
    }
}

function saveFullCollapseState(state) {
    try {
        localStorage.setItem(getStorageKey(), JSON.stringify(state));
    } catch {
        // ignore storage errors
    }
}

// Determine if a line is applicable for a given tax line based on scope + pivot data.
function isTaxLineApplicable(taxLine, rowId, rowEntityType) {
    const scope = taxLine.application_scope;
    if (scope === 'all_revenues') return ['revenue', 'sponsor', 'ticketing'].includes(rowEntityType);
    if (scope === 'all_expenditure') return ['expense', 'position'].includes(rowEntityType);
    if (scope === 'workforce_lines_only') return rowEntityType === 'position';
    if (scope === 'specific_budget_lines') {
        if (rowEntityType === 'revenue') return (taxLine.applicable_revenue_ids ?? []).includes(rowId);
        if (rowEntityType === 'sponsor') return (taxLine.applicable_sponsor_ids ?? []).includes(rowId);
        if (rowEntityType === 'ticketing') return !!taxLine.applicable_ticketing;
        if (rowEntityType === 'expense') return (taxLine.applicable_expense_ids ?? []).includes(rowId);
        if (rowEntityType === 'position') return (taxLine.applicable_position_ids ?? []).includes(rowId);
    }
    return false;
}

export default function BudgetCodesIndex() {
    const toast = useToast();
    const [isGrouped, setIsGrouped] = useState(false);
    const [groups, setGroups] = useState([]);
    const [glCodes, setGlCodes] = useState({});
    const [showedGroups, setShowedGroups] = useState({});
    const [showedExpenses, setShowedExpenses] = useState({
        projects: {},
        templates: {},
    });
    const [showedWorkforce, setShowedWorkforce] = useState({});
    const [showedPacks, setShowedPacks] = useState({});
    const [codes, setCodes] = useState({});
    const [revenueLines, setRevenueLines] = useState({ revenues: [], sponsors: [], ticketing: null });
    const [taxLines, setTaxLines] = useState([]);
    const [vatGstEnabled, setVatGstEnabled] = useState(true);
    // Revenue section collapse state: revenues/sponsors/ticketing = independent collapsible subsections
    const [showRevenues, setShowRevenues] = useState({ revenues: true, sponsors: true, ticketing: true });
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    // delete-multiple state: 'idle' | 'choose' | 'selecting'
    const [deleteStage, setDeleteStage] = useState('idle');
    const [selectedIds, setSelectedIds] = useState([]);

    // bulk-assign state: 'idle' | 'selecting-gl' | 'selecting-gst' | 'selecting-tax'
    const [bulkStage, setBulkStage] = useState('idle');
    const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
    // For revenue bulk selections: [{id, type}]
    const [bulkSelectedRevLines, setBulkSelectedRevLines] = useState([]);
    const [bulkGlCodeId, setBulkGlCodeId] = useState('');
    const [bulkGstValue, setBulkGstValue] = useState(true);
    const [bulkTaxLineIds, setBulkTaxLineIds] = useState([]);
    const [bulkTaxApplicable, setBulkTaxApplicable] = useState(true);
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const collapseStateRef = useRef({ groups: {}, templates: {}, projects: {}, projectTemplates: {}, packs: {}, workforce: {} });

    function persistCollapseState(updates) {
        collapseStateRef.current = { ...collapseStateRef.current, ...updates };
        saveFullCollapseState(collapseStateRef.current);
    }

    // Pagination — client-side over the groups (departments) array
    const savedPageRef = useRef(null);
    const pagination = usePagination({ perPage: 25 });
    const { page, perPage, lastPage, from, to, total } = pagination;

    // Derive the visible slice of groups for the current page
    const pagedGroups = useMemo(() => {
        const start = (page - 1) * perPage;
        return groups.slice(start, start + perPage);
    }, [groups, page, perPage]);

    // Keep pagination meta in sync when groups change
    useEffect(() => {
        const totalGroups = groups.length;
        const lp = Math.max(1, Math.ceil(totalGroups / perPage));
        const start = (page - 1) * perPage + 1;
        const end = Math.min(page * perPage, totalGroups);
        pagination.setLastPage(lp);
        pagination.setTotal(totalGroups);
        pagination.setFrom(totalGroups > 0 ? start : 0);
        pagination.setTo(totalGroups > 0 ? end : 0);
    }, [groups, page, perPage]);

    useEffect(fetchCodes, []);

    // Dynamic colSpan including GST (when enabled) and tax line columns
    const colSpan = COL_SPAN_BASE + (vatGstEnabled ? 1 : 0) + taxLines.length;

    function fetchCodes() {
        setIsLoading(true);
        axios
            .get(indexUrl)
            .then((res) => processResponseData(res.data))
            .catch(() => clearData())
            .finally(() => setIsLoading(false));
    }

    function fetchCodesPreservingPage() {
        savedPageRef.current = page;
        fetchCodes();
    }

    function handleDownload() {
        setIsDownloading(true);
        axios({
            url: exportBudgetCodesUrl,
            method: 'POST',
            responseType: 'blob',
            data: { _token: csrfToken },
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'budget-lines.xlsx');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                console.error(err);
                alert($t('Failed to download the export file.'));
            })
            .finally(() => setIsDownloading(false));
    }

    function processResponseData(data) {
        const { codes, gl_codes, revenue_lines, tax_lines, vat_gst_enabled } = data;
        setGlCodes(gl_codes);
        setRevenueLines(revenue_lines ?? { revenues: [], sponsors: [], ticketing: null });
        setTaxLines(tax_lines ?? []);
        setVatGstEnabled(vat_gst_enabled !== false);

        const stored = loadFullCollapseState();

        const showed = JSON.parse(JSON.stringify(showedExpenses));
        const sPacks = {};
        Object.values(codes).forEach((groupData) => {
            const projects = !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0 ? groupData.projects : {};
            const templates = !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0 ? groupData.templates : {};
            const packs = !Array.isArray(groupData.packs) && Object.keys(groupData.packs ?? {}).length > 0 ? groupData.packs : {};

            Object.values(templates).forEach((item) => {
                const templateId = item?.template?.id;
                const def = stored?.templates?.[String(templateId)] ?? false;
                showed.templates[templateId] = showed.templates[templateId] ?? { expanded: def };
            });

            Object.values(packs).forEach((item) => {
                const packId = item?.pack?.id;
                if (packId != null) {
                    sPacks[packId] = stored?.packs?.[String(packId)] ?? false;
                }
            });

            Object.values(projects).forEach((item) => {
                const projectId = item?.project?.id;
                const pTemplates = !Array.isArray(item.templates) && Object.keys(item.templates).length > 0 ? item.templates : {};
                const pPacks = !Array.isArray(item.packs) && Object.keys(item.packs ?? {}).length > 0 ? item.packs : {};

                const projDef = stored?.projects?.[String(projectId)] ?? false;
                showed.projects[projectId] = showed.projects[projectId] ?? { expanded: projDef, templates: {} };

                Object.values(pTemplates).forEach((pItem) => {
                    const templateId = pItem?.template?.id;
                    const pTplDef = stored?.projectTemplates?.[String(templateId)] ?? false;
                    showed.projects[projectId].templates[templateId] = showed.projects[projectId].templates[templateId] ?? { expanded: pTplDef };
                });

                Object.values(pPacks).forEach((pItem) => {
                    const packId = pItem?.pack?.id;
                    if (packId != null) {
                        sPacks[packId] = stored?.packs?.[String(packId)] ?? false;
                    }
                });
            });
        });

        setShowedExpenses(showed);
        setShowedPacks(sPacks);
        setCodes(codes);

        const groupKeys = Object.keys(codes);
        setGroups(groupKeys);

        // Restore saved page after bulk refresh, otherwise stay on page 1
        if (savedPageRef.current !== null) {
            const lp = Math.max(1, Math.ceil(groupKeys.length / perPage));
            pagination.setPage(Math.min(savedPageRef.current, lp));
            savedPageRef.current = null;
        }

        const sGroups = {};
        groupKeys.forEach((groupKey) => {
            sGroups[groupKey] = stored?.groups?.[groupKey] ?? false;
        });
        setShowedGroups(sGroups);

        const sWorkforce = {};
        groupKeys.forEach((groupKey) => {
            const hasPositions = (codes[groupKey]?.items ?? []).some((i) => i.entity_type === 'position');
            if (hasPositions) {
                sWorkforce[groupKey] = stored?.workforce?.[groupKey] ?? false;
            }
        });
        setShowedWorkforce(sWorkforce);

        const flatTemplates = {};
        Object.keys(showed.templates).forEach((id) => {
            flatTemplates[id] = !!showed.templates[id].expanded;
        });
        const flatProjects = {};
        const flatProjTemplates = {};
        Object.keys(showed.projects).forEach((id) => {
            flatProjects[id] = !!showed.projects[id].expanded;
            Object.keys(showed.projects[id].templates ?? {}).forEach((tplId) => {
                flatProjTemplates[tplId] = !!showed.projects[id].templates[tplId].expanded;
            });
        });
        collapseStateRef.current = {
            groups: sGroups,
            templates: flatTemplates,
            projects: flatProjects,
            projectTemplates: flatProjTemplates,
            packs: sPacks,
            workforce: sWorkforce,
            revenues: stored?.revenues ?? { revenues: true, sponsors: true, ticketing: true },
        };

        setShowRevenues(collapseStateRef.current.revenues);
        setIsGrouped(true);
    }

    function clearData() {
        setIsGrouped(false);
        setGroups([]);
        setCodes({});
        setShowedGroups({});
        setRevenueLines({ revenues: [], sponsors: [], ticketing: null });
        setTaxLines([]);
        setVatGstEnabled(true);
        setShowRevenues({ revenues: true, sponsors: true, ticketing: true });
    }

    function toggleGroup(group) {
        setShowedGroups((prev) => {
            const next = { ...prev, [group]: !prev[group] };
            persistCollapseState({ groups: next });
            return next;
        });
    }

    function expandAll() {
        const nextGroups = {};
        Object.keys(showedGroups).forEach((k) => {
            nextGroups[k] = true;
        });
        const nextRevenues = { revenues: true, sponsors: true, ticketing: true };
        setShowedGroups(nextGroups);
        setShowRevenues(nextRevenues);
        persistCollapseState({ groups: nextGroups, revenues: nextRevenues });
    }

    function collapseAll() {
        const nextGroups = {};
        Object.keys(showedGroups).forEach((k) => {
            nextGroups[k] = false;
        });

        const nextWf = {};
        Object.keys(showedWorkforce).forEach((k) => {
            nextWf[k] = false;
        });

        const nextPacks = {};
        Object.keys(showedPacks).forEach((k) => {
            nextPacks[k] = false;
        });

        const nextExp = JSON.parse(JSON.stringify(showedExpenses));
        Object.keys(nextExp.templates).forEach((id) => {
            nextExp.templates[id].expanded = false;
        });
        Object.keys(nextExp.projects).forEach((id) => {
            nextExp.projects[id].expanded = false;
            Object.keys(nextExp.projects[id].templates ?? {}).forEach((tplId) => {
                nextExp.projects[id].templates[tplId].expanded = false;
            });
        });

        const flatTemplates = {};
        Object.keys(nextExp.templates).forEach((id) => {
            flatTemplates[id] = false;
        });
        const flatProjects = {};
        const flatProjTemplates = {};
        Object.keys(nextExp.projects).forEach((id) => {
            flatProjects[id] = false;
            Object.keys(nextExp.projects[id].templates ?? {}).forEach((tplId) => {
                flatProjTemplates[tplId] = false;
            });
        });

        const nextRevenues = { revenues: false, sponsors: false, ticketing: false };

        setShowedGroups(nextGroups);
        setShowedWorkforce(nextWf);
        setShowedPacks(nextPacks);
        setShowedExpenses(nextExp);
        setShowRevenues(nextRevenues);

        persistCollapseState({
            groups: nextGroups,
            workforce: nextWf,
            packs: nextPacks,
            templates: flatTemplates,
            projects: flatProjects,
            projectTemplates: flatProjTemplates,
            revenues: nextRevenues,
        });
    }

    function toggleWorkforce(group) {
        setShowedWorkforce((prev) => {
            const next = { ...prev, [group]: !prev[group] };
            persistCollapseState({ workforce: next });
            return next;
        });
    }

    function togglePack(packId) {
        setShowedPacks((prev) => {
            const next = { ...prev, [packId]: !prev[packId] };
            persistCollapseState({ packs: next });
            return next;
        });
    }

    function toggleRevenueSection(key) {
        setShowRevenues((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            persistCollapseState({ revenues: next });
            return next;
        });
    }

    function handleWorkforceGlChange(group, glCodeId) {
        const positions = (codes[group]?.items ?? []).filter((i) => i.entity_type === 'position');
        const position_ids = positions.map((p) => p.id);
        if (position_ids.length === 0) return;
        const previousGlCodeIds = positions.map((p) => ({ id: p.id, gl_code_id: p.gl_code_id ?? null }));
        position_ids.forEach((id) => updateCodeGlCodeLocally(id, glCodeId));
        axios.put(bulkAssignGlCodeUrl, { expense_ids: [], position_ids, gl_code_id: glCodeId || null }).catch((err) => {
            console.error(err);
            toast.error($t('Failed to assign GL Code to workforce.'));
            previousGlCodeIds.forEach(({ id, gl_code_id }) => updateCodeGlCodeLocally(id, gl_code_id));
        });
    }

    function handleWorkforceGstChange(group, checked) {
        const positions = (codes[group]?.items ?? []).filter((i) => i.entity_type === 'position');
        const position_ids = positions.map((p) => p.id);
        if (position_ids.length === 0) return;
        const previousGstValues = positions.map((p) => ({ id: p.id, gst_vat_applicable: !!p.gst_vat_applicable }));
        position_ids.forEach((id) => updateCodeGstLocally(id, 'position', { gst_vat_applicable: checked, gst_vat_effective: checked }));
        axios.put(bulkAssignGstUrl, { expense_ids: [], position_ids, gst_vat_applicable: checked }).catch((err) => {
            console.error(err);
            toast.error($t('Failed to update GST/VAT for workforce.'));
            previousGstValues.forEach(({ id, gst_vat_applicable }) =>
                updateCodeGstLocally(id, 'position', { gst_vat_applicable, gst_vat_effective: gst_vat_applicable }),
            );
        });
    }

    const toggleExpense = (tplId = null, projectId = null, pTplId) => {
        const showed = JSON.parse(JSON.stringify(showedExpenses));

        if (tplId) {
            showed.templates[tplId].expanded = !showed.templates[tplId].expanded;
        } else if (projectId) {
            if (pTplId) {
                showed.projects[projectId].templates[pTplId].expanded = !showed.projects[projectId].templates[pTplId].expanded;
            } else {
                showed.projects[projectId].expanded = !showed.projects[projectId].expanded;
            }
        }

        setShowedExpenses(showed);

        const flatTemplates = {};
        Object.keys(showed.templates).forEach((id) => {
            flatTemplates[id] = !!showed.templates[id].expanded;
        });
        const flatProjects = {};
        const flatProjTemplates = {};
        Object.keys(showed.projects).forEach((id) => {
            flatProjects[id] = !!showed.projects[id].expanded;
            Object.keys(showed.projects[id].templates ?? {}).forEach((pTplId) => {
                flatProjTemplates[pTplId] = !!showed.projects[id].templates[pTplId].expanded;
            });
        });
        persistCollapseState({ templates: flatTemplates, projects: flatProjects, projectTemplates: flatProjTemplates });
    };

    const displayCodes = (group, tplId = null, projectId = null, pTplId) => {
        if (!showedGroups[group]) {
            return false;
        }
        if (tplId) {
            return showedExpenses.templates[tplId].expanded;
        }

        if (projectId && pTplId) {
            return showedExpenses.projects[projectId].expanded && showedExpenses.projects[projectId].templates[pTplId].expanded;
        }

        if (projectId && !pTplId) {
            return showedExpenses.projects[projectId].expanded;
        }

        return true;
    };

    function handleDeleteMultipleClick() {
        const msg =
            'Choosing to delete multiple budget lines cannot be undone. ' +
            'Only expense lines that do not have commitments attached to them can be deleted. ' +
            'Expenses packs must be deleted using the Manage Expense packs feature. ' +
            'Workforce positions must be deleted from the Workforce - Positions Report. ' +
            'Do you want to proceed?';
        if (window.confirm(msg)) {
            setDeleteStage('choose');
        }
    }

    function handleStartDeleteMany() {
        setDeleteStage('selecting');
        setSelectedIds([]);
    }

    function handleStartDeleteAll() {
        // collect every selectable code
        const all = [];
        groups.forEach((group) => {
            const groupData = codes[group];

            groupData.items.forEach((item) => {
                all.push(item.id);
            });

            const projects = !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0 ? groupData.projects : {};
            const templates = !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0 ? groupData.templates : {};
            const packs = !Array.isArray(groupData.packs) && Object.keys(groupData.packs ?? {}).length > 0 ? groupData.packs : {};

            Object.values(templates).forEach((tpl) => {
                all.push(tpl.template.id);
                tpl.items.forEach((item) => {
                    all.push(item.id);
                });
            });

            Object.values(packs).forEach((packData) => {
                all.push(packData.pack.id);
                (packData.items ?? []).forEach((item) => {
                    all.push(item.id);
                });
            });

            Object.values(projects).forEach((project) => {
                all.push(project.project.id);
                project.items.forEach((item) => {
                    all.push(item.id);
                });

                const pTemplates = !Array.isArray(project.templates) && Object.keys(project.templates).length > 0 ? project.templates : {};
                Object.values(pTemplates).forEach((tpl) => {
                    all.push(tpl.template.id);
                    tpl.items.forEach((item) => {
                        all.push(item.id);
                    });
                });

                const pPacks = !Array.isArray(project.packs) && Object.keys(project.packs ?? {}).length > 0 ? project.packs : {};
                Object.values(pPacks).forEach((packData) => {
                    all.push(packData.pack.id);
                    (packData.items ?? []).forEach((item) => {
                        all.push(item.id);
                    });
                });
            });
        });
        setDeleteStage('selecting');
        setSelectedIds(all);
    }

    // build a quick lookup from id to code object
    const idToCode = useMemo(() => {
        const map = {};
        Object.values(codes).forEach((groupCodes) => {
            Object.values(groupCodes.projects).forEach((projectData) => {
                if (projectData.project) {
                    map[projectData.project.id] = projectData.project;
                }
            });
        });
        return map;
    }, [codes]);

    // find prefixes of any selected Project, e.g. "PE0003."
    const selectedProjectPrefixes = useMemo(() => {
        return selectedIds
            .map((id) => {
                const c = idToCode[id];
                return c && c.type === 'Project' ? c.reference_code + '.' : null;
            })
            .filter(Boolean);
    }, [selectedIds, idToCode]);

    // toggle a row; if it's a Project, only keep the project ID (drop its children)
    function handleToggleRow(code) {
        setSelectedIds((prev) => {
            const isSelected = prev.includes(code.id);

            if (code.type === 'Project') {
                const prefix = code.reference_code + '.';
                if (!isSelected) {
                    // selecting Project: remove any of its children, then add project ID
                    const cleaned = prev.filter((id) => {
                        const c = idToCode[id];
                        return !(c && c.reference_code.startsWith(prefix));
                    });
                    return [...cleaned, code.id];
                } else {
                    // deselecting Project: just remove it
                    return prev.filter((id) => id !== code.id);
                }
            }

            // normal toggle for non-Project rows
            return isSelected ? prev.filter((id) => id !== code.id) : [...prev, code.id];
        });
    }

    function handleCancelDelete() {
        setDeleteStage('idle');
        setSelectedIds([]);
    }

    function handleSubmitDelete() {
        if (!window.confirm('Are you sure you want to delete the selected items?')) {
            return;
        }
        setIsDeleteLoading(true);
        axios
            .post(deleteManyBudgetCodesUrl, { _token: csrfToken, deleteIds: selectedIds })
            .then(({ data }) => {
                if ((data?.not_deleted?.length ?? 0) < selectedIds.length) {
                    toast.success($t('The request to delete selected expenses is being processed. This may take a few minutes'));
                }
                if (data?.not_deleted?.length) {
                    toast.error(data.message);
                }
                setIsDeleteLoading(false);
                fetchCodesPreservingPage();
                handleCancelDelete();
            })
            .catch((err) => {
                setIsDeleteLoading(false);
                console.error(err);
                alert('Failed to delete selected budget lines.');
            });
    }

    // ─── Bulk assign helpers ────────────────────────────────────────────

    // When requireSelectable=true: only include items where is_selectable=true (used for GL code bulk).
    // When requireSelectable=false: include all non-child items (used for GST/tax bulk).
    function collectAllBulkSelectableIds(requireSelectable = true) {
        const all = [];
        groups.forEach((group) => {
            const groupData = codes[group];

            (groupData.items ?? []).forEach((item) => {
                if (!item.is_pack_child && !item.is_template_child && (!requireSelectable || item.is_selectable)) {
                    all.push(item.id);
                }
            });

            const projects = !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0 ? groupData.projects : {};
            const templates = !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0 ? groupData.templates : {};
            const packs = !Array.isArray(groupData.packs) && Object.keys(groupData.packs ?? {}).length > 0 ? groupData.packs : {};

            Object.values(templates).forEach((tpl) => {
                if (!tpl.template.is_pack_child && !tpl.template.is_template_child && (!requireSelectable || tpl.template.is_selectable)) {
                    all.push(tpl.template.id);
                }
                (tpl.items ?? []).forEach((item) => {
                    if (!item.is_pack_child && !item.is_template_child && (!requireSelectable || item.is_selectable)) {
                        all.push(item.id);
                    }
                });
            });

            Object.values(packs).forEach((packData) => {
                if (!packData.pack.is_pack_child && !packData.pack.is_template_child && (!requireSelectable || packData.pack.is_selectable)) {
                    all.push(packData.pack.id);
                }
            });

            Object.values(projects).forEach((project) => {
                if (!project.project.is_pack_child && !project.project.is_template_child && (!requireSelectable || project.project.is_selectable)) {
                    all.push(project.project.id);
                }
                (project.items ?? []).forEach((item) => {
                    if (!item.is_pack_child && !item.is_template_child && (!requireSelectable || item.is_selectable)) {
                        all.push(item.id);
                    }
                });

                const pTemplates = !Array.isArray(project.templates) && Object.keys(project.templates).length > 0 ? project.templates : {};
                Object.values(pTemplates).forEach((tpl) => {
                    if (!tpl.template.is_pack_child && !tpl.template.is_template_child && (!requireSelectable || tpl.template.is_selectable)) {
                        all.push(tpl.template.id);
                    }
                    (tpl.items ?? []).forEach((item) => {
                        if (!item.is_pack_child && !item.is_template_child && (!requireSelectable || item.is_selectable)) {
                            all.push(item.id);
                        }
                    });
                });

                const pPacks = !Array.isArray(project.packs) && Object.keys(project.packs ?? {}).length > 0 ? project.packs : {};
                Object.values(pPacks).forEach((packData) => {
                    if (!packData.pack.is_pack_child && !packData.pack.is_template_child && (!requireSelectable || packData.pack.is_selectable)) {
                        all.push(packData.pack.id);
                    }
                });
            });
        });
        return all;
    }

    // Build a map of id → entity_type from the current codes state
    function buildEntityTypeMap() {
        const map = {};
        Object.values(codes).forEach((groupData) => {
            (groupData.items ?? []).forEach((item) => {
                map[item.id] = item.entity_type ?? 'expense';
            });
            const templates = !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0 ? groupData.templates : {};
            Object.values(templates).forEach((tpl) => {
                map[tpl.template.id] = tpl.template.entity_type ?? 'expense';
                (tpl.items ?? []).forEach((item) => {
                    map[item.id] = item.entity_type ?? 'expense';
                });
            });
            const packs = !Array.isArray(groupData.packs) && Object.keys(groupData.packs ?? {}).length > 0 ? groupData.packs : {};
            Object.values(packs).forEach((packData) => {
                map[packData.pack.id] = packData.pack.entity_type ?? 'expense';
                (packData.items ?? []).forEach((item) => {
                    map[item.id] = item.entity_type ?? 'expense';
                });
            });
            const projects = !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0 ? groupData.projects : {};
            Object.values(projects).forEach((project) => {
                map[project.project.id] = project.project.entity_type ?? 'expense';
                (project.items ?? []).forEach((item) => {
                    map[item.id] = item.entity_type ?? 'expense';
                });
                const pTemplates = !Array.isArray(project.templates) && Object.keys(project.templates).length > 0 ? project.templates : {};
                Object.values(pTemplates).forEach((tpl) => {
                    map[tpl.template.id] = tpl.template.entity_type ?? 'expense';
                    (tpl.items ?? []).forEach((item) => {
                        map[item.id] = item.entity_type ?? 'expense';
                    });
                });
                const pPacks = !Array.isArray(project.packs) && Object.keys(project.packs ?? {}).length > 0 ? project.packs : {};
                Object.values(pPacks).forEach((packData) => {
                    map[packData.pack.id] = packData.pack.entity_type ?? 'expense';
                    (packData.items ?? []).forEach((item) => {
                        map[item.id] = item.entity_type ?? 'expense';
                    });
                });
            });
        });
        return map;
    }

    // Split an array of IDs into expense_ids and position_ids using the entity_type map
    function splitIdsByEntityType(ids) {
        const entityTypeMap = buildEntityTypeMap();
        const expense_ids = [];
        const position_ids = [];
        ids.forEach((id) => {
            if (entityTypeMap[id] === 'position') {
                position_ids.push(id);
            } else {
                expense_ids.push(id);
            }
        });
        return { expense_ids, position_ids };
    }

    function handleToggleBulkRow(id, isChild) {
        if (isChild) return;
        setBulkSelectedIds((prev) => {
            return prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
        });
    }

    function handleToggleBulkRevLine(id, type) {
        setBulkSelectedRevLines((prev) => {
            const exists = prev.some((r) => r.id === id && r.type === type);
            return exists ? prev.filter((r) => !(r.id === id && r.type === type)) : [...prev, { id, type }];
        });
    }

    function handleCancelBulk() {
        setBulkStage('idle');
        setBulkSelectedIds([]);
        setBulkSelectedRevLines([]);
        setBulkGlCodeId('');
        setBulkTaxLineIds([]);
    }

    function handleStartBulkGl() {
        setBulkStage('selecting-gl');
        setBulkSelectedIds([]);
        setBulkSelectedRevLines([]);
    }

    function handleStartBulkGst() {
        setBulkStage('selecting-gst');
        setBulkSelectedIds([]);
        setBulkSelectedRevLines([]);
    }

    function handleStartBulkTax() {
        setBulkStage('selecting-tax');
        setBulkSelectedIds([]);
        setBulkSelectedRevLines([]);
        setBulkTaxLineIds([]);
    }

    function handleSelectAllPageBulk() {
        expandAll();
        // For GL code bulk: only selectable items. For GST/tax bulk: all non-child items.
        const requireSelectable = bulkStage === 'selecting-gl';
        setBulkSelectedIds(collectAllBulkSelectableIds(requireSelectable));
        // Also select all revenue lines
        const allRev = [
            ...(revenueLines.revenues ?? []).map((r) => ({ id: r.id, type: 'revenue' })),
            ...(revenueLines.sponsors ?? []).map((s) => ({ id: s.id, type: 'sponsor' })),
            ...(revenueLines.ticketing ? [{ id: revenueLines.ticketing.id, type: 'ticketing' }] : []),
        ];
        setBulkSelectedRevLines(allRev);
    }

    function handleSubmitBulkGl() {
        if (!bulkGlCodeId) {
            alert($t('Please select a GL Code to assign.'));
            return;
        }
        if (bulkSelectedIds.length === 0) {
            alert($t('Please select at least one budget line.'));
            return;
        }
        setIsBulkLoading(true);
        const { expense_ids, position_ids } = splitIdsByEntityType(bulkSelectedIds);
        axios
            .put(bulkAssignGlCodeUrl, { expense_ids, position_ids, gl_code_id: bulkGlCodeId })
            .then(() => {
                toast.success($t('GL Code assigned to selected budget lines.'));
                fetchCodesPreservingPage();
                handleCancelBulk();
            })
            .catch((err) => {
                console.error(err);
                toast.error($t('Failed to assign GL Code.'));
            })
            .finally(() => setIsBulkLoading(false));
    }

    function handleSubmitBulkGst() {
        if (bulkSelectedIds.length === 0 && bulkSelectedRevLines.length === 0) {
            alert($t('Please select at least one budget line.'));
            return;
        }
        setIsBulkLoading(true);
        const { expense_ids, position_ids } = splitIdsByEntityType(bulkSelectedIds);
        const revenue_ids = bulkSelectedRevLines.filter((r) => r.type === 'revenue').map((r) => r.id);
        const sponsor_ids = bulkSelectedRevLines.filter((r) => r.type === 'sponsor').map((r) => r.id);
        const ticketing_ids = bulkSelectedRevLines.filter((r) => r.type === 'ticketing').map((r) => r.id);
        axios
            .put(bulkAssignGstUrl, { expense_ids, position_ids, revenue_ids, sponsor_ids, ticketing_ids, gst_vat_applicable: bulkGstValue })
            .then(() => {
                toast.success($t('GST/VAT Applicable updated for selected budget lines.'));
                fetchCodesPreservingPage();
                handleCancelBulk();
            })
            .catch((err) => {
                console.error(err);
                toast.error($t('Failed to update GST/VAT Applicable.'));
            })
            .finally(() => setIsBulkLoading(false));
    }

    function handleSubmitBulkTax() {
        if (bulkTaxLineIds.length === 0) {
            alert($t('Please select at least one tax line.'));
            return;
        }
        if (bulkSelectedIds.length === 0 && bulkSelectedRevLines.length === 0) {
            alert($t('Please select at least one budget line.'));
            return;
        }
        setIsBulkLoading(true);
        const { expense_ids, position_ids } = splitIdsByEntityType(bulkSelectedIds);
        axios
            .post(taxLineBulkToggleUrl, {
                tax_line_ids: bulkTaxLineIds,
                applicable: bulkTaxApplicable,
                revenue_lines: bulkSelectedRevLines,
                expense_ids,
                position_ids,
            })
            .then(() => {
                toast.success($t('Tax line assignment updated.'));
                fetchCodesPreservingPage();
                handleCancelBulk();
            })
            .catch((err) => {
                console.error(err);
                toast.error($t('Failed to update tax line assignment.'));
            })
            .finally(() => setIsBulkLoading(false));
    }

    const getGlCode = (id) => {
        if (!id) {
            return null;
        }

        return glCodes[id] ?? null;
    };

    // Update local codes state for GL code change without re-fetching
    const updateCodeGlCodeLocally = (itemId, glCodeId) => {
        setCodes((prevCodes) => {
            const next = JSON.parse(JSON.stringify(prevCodes));
            Object.values(next).forEach((groupData) => {
                // items
                groupData.items = (groupData.items ?? []).map((item) => (item.id === itemId ? { ...item, gl_code_id: glCodeId } : item));
                // templates
                const templates = !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0 ? groupData.templates : {};
                Object.values(templates).forEach((tpl) => {
                    if (tpl.template.id === itemId) tpl.template = { ...tpl.template, gl_code_id: glCodeId };
                    tpl.items = (tpl.items ?? []).map((item) => (item.id === itemId ? { ...item, gl_code_id: glCodeId } : item));
                });
                // packs
                const packs = !Array.isArray(groupData.packs) && Object.keys(groupData.packs ?? {}).length > 0 ? groupData.packs : {};
                Object.values(packs).forEach((packData) => {
                    if (packData.pack.id === itemId) packData.pack = { ...packData.pack, gl_code_id: glCodeId };
                    packData.items = (packData.items ?? []).map((item) => (item.id === itemId ? { ...item, gl_code_id: glCodeId } : item));
                });
                // projects
                const projects = !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0 ? groupData.projects : {};
                Object.values(projects).forEach((projectData) => {
                    if (projectData.project.id === itemId) projectData.project = { ...projectData.project, gl_code_id: glCodeId };
                    projectData.items = (projectData.items ?? []).map((item) => (item.id === itemId ? { ...item, gl_code_id: glCodeId } : item));
                    const pTemplates = !Array.isArray(projectData.templates) && Object.keys(projectData.templates).length > 0 ? projectData.templates : {};
                    Object.values(pTemplates).forEach((tpl) => {
                        if (tpl.template.id === itemId) tpl.template = { ...tpl.template, gl_code_id: glCodeId };
                        tpl.items = (tpl.items ?? []).map((item) => (item.id === itemId ? { ...item, gl_code_id: glCodeId } : item));
                    });
                    const pPacks = !Array.isArray(projectData.packs) && Object.keys(projectData.packs ?? {}).length > 0 ? projectData.packs : {};
                    Object.values(pPacks).forEach((packData) => {
                        if (packData.pack.id === itemId) packData.pack = { ...packData.pack, gl_code_id: glCodeId };
                        packData.items = (packData.items ?? []).map((item) => (item.id === itemId ? { ...item, gl_code_id: glCodeId } : item));
                    });
                });
            });
            return next;
        });
    };

    // Update local codes state for GST change without re-fetching. `patch` holds the line's own,
    // effective and inherited values; a project's inheriting lines take its new effective value.
    const updateCodeGstLocally = (itemId, entityType, patch) => {
        const matches = (item) => item.id === itemId && (item.entity_type ?? 'expense') === entityType;
        const apply = (item) => (matches(item) ? { ...item, ...patch } : item);
        const inherit = (item) => (item.gst_vat_inherited ? { ...item, gst_vat_effective: patch.gst_vat_effective } : item);

        setCodes((prevCodes) => {
            const next = JSON.parse(JSON.stringify(prevCodes));
            Object.values(next).forEach((groupData) => {
                groupData.items = (groupData.items ?? []).map(apply);
                const templates = !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0 ? groupData.templates : {};
                Object.values(templates).forEach((tpl) => {
                    tpl.template = apply(tpl.template);
                    tpl.items = (tpl.items ?? []).map(apply);
                });
                const packs = !Array.isArray(groupData.packs) && Object.keys(groupData.packs ?? {}).length > 0 ? groupData.packs : {};
                Object.values(packs).forEach((packData) => {
                    packData.pack = apply(packData.pack);
                    packData.items = (packData.items ?? []).map(apply);
                });
                const projects = !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0 ? groupData.projects : {};
                Object.values(projects).forEach((projectData) => {
                    const isToggledProject = matches(projectData.project);
                    projectData.project = apply(projectData.project);
                    projectData.items = (projectData.items ?? []).map((item) => (isToggledProject ? inherit(item) : apply(item)));
                    const pTemplates = !Array.isArray(projectData.templates) && Object.keys(projectData.templates).length > 0 ? projectData.templates : {};
                    Object.values(pTemplates).forEach((tpl) => {
                        tpl.template = isToggledProject ? inherit(tpl.template) : apply(tpl.template);
                        tpl.items = (tpl.items ?? []).map(apply);
                    });
                    const pPacks = !Array.isArray(projectData.packs) && Object.keys(projectData.packs ?? {}).length > 0 ? projectData.packs : {};
                    Object.values(pPacks).forEach((packData) => {
                        packData.pack = isToggledProject ? inherit(packData.pack) : apply(packData.pack);
                        packData.items = (packData.items ?? []).map(apply);
                    });
                });
            });
            return next;
        });
    };

    // Optimistic update for revenue GST change
    const updateRevLineGstLocally = (id, type, gstValue) => {
        setRevenueLines((prev) => {
            if (type === 'revenue') {
                return { ...prev, revenues: prev.revenues.map((r) => (r.id === id ? { ...r, gst_vat_applicable: gstValue } : r)) };
            }
            if (type === 'sponsor') {
                return { ...prev, sponsors: prev.sponsors.map((s) => (s.id === id ? { ...s, gst_vat_applicable: gstValue } : s)) };
            }
            if (type === 'ticketing' && prev.ticketing) {
                return { ...prev, ticketing: { ...prev.ticketing, gst_vat_applicable: gstValue } };
            }
            return prev;
        });
    };

    const onChangeGlCode = (e, data) => {
        const val = e.target.value;
        const editedItem = JSON.parse(JSON.stringify(data));
        editedItem.gl_code_id = val;

        // Optimistically update local state (no table refresh)
        updateCodeGlCodeLocally(data.id, val);

        axios.put(editUrl, editedItem).catch((err) => {
            console.log(err);
            // Revert on failure
            updateCodeGlCodeLocally(data.id, data.gl_code_id);
        });
    };

    const onChangeGst = (checked, item) => {
        // Optimistically update local state (no table refresh)
        updateCodeGstLocally(item.id, item.entity_type ?? 'expense', { gst_vat_applicable: checked, gst_vat_effective: checked, gst_vat_inherited: false });

        axios
            .put(updateGstUrl, {
                id: item.id,
                entity_type: item.entity_type ?? 'expense',
                gst_vat_applicable: checked,
            })
            .catch((err) => {
                console.error(err);
                // Revert on failure
                updateCodeGstLocally(item.id, item.entity_type ?? 'expense', {
                    gst_vat_applicable: item.gst_vat_applicable,
                    gst_vat_effective: isGstEffective(item),
                    gst_vat_inherited: !!item.gst_vat_inherited,
                });
            });
    };

    const onChangeRevGst = (checked, item) => {
        updateRevLineGstLocally(item.id, item.entity_type, checked);
        axios
            .put(updateGstUrl, {
                id: item.id,
                entity_type: item.entity_type,
                gst_vat_applicable: checked,
            })
            .catch((err) => {
                console.error(err);
                updateRevLineGstLocally(item.id, item.entity_type, !checked);
            });
    };

    const onChangeTaxLineToggle = (taxLineId, rowId, rowEntityType, checked) => {
        const isRevenue = ['revenue', 'sponsor', 'ticketing'].includes(rowEntityType);
        const url = isRevenue ? taxLineRevenueToggleUrl : taxLineExpenseToggleUrl;
        const payload = isRevenue
            ? { tax_line_id: taxLineId, revenue_line_id: rowId, revenue_line_type: rowEntityType, applicable: checked }
            : { tax_line_id: taxLineId, expense_line_id: rowId, expense_line_type: rowEntityType, applicable: checked };

        // Optimistic update
        setTaxLines((prev) =>
            prev.map((tl) => {
                if (tl.id !== taxLineId) return tl;
                if (rowEntityType === 'revenue') {
                    return {
                        ...tl,
                        applicable_revenue_ids: checked
                            ? [...(tl.applicable_revenue_ids ?? []), rowId]
                            : (tl.applicable_revenue_ids ?? []).filter((i) => i !== rowId),
                    };
                }
                if (rowEntityType === 'sponsor') {
                    return {
                        ...tl,
                        applicable_sponsor_ids: checked
                            ? [...(tl.applicable_sponsor_ids ?? []), rowId]
                            : (tl.applicable_sponsor_ids ?? []).filter((i) => i !== rowId),
                    };
                }
                if (rowEntityType === 'ticketing') {
                    return { ...tl, applicable_ticketing: checked };
                }
                if (rowEntityType === 'expense') {
                    return {
                        ...tl,
                        applicable_expense_ids: checked
                            ? [...(tl.applicable_expense_ids ?? []), rowId]
                            : (tl.applicable_expense_ids ?? []).filter((i) => i !== rowId),
                    };
                }
                if (rowEntityType === 'position') {
                    return {
                        ...tl,
                        applicable_position_ids: checked
                            ? [...(tl.applicable_position_ids ?? []), rowId]
                            : (tl.applicable_position_ids ?? []).filter((i) => i !== rowId),
                    };
                }
                return tl;
            }),
        );

        axios.post(url, payload).catch((err) => {
            console.error(err);
            toast.error($t('Failed to update tax line.'));
            // Revert — refetch to get clean state
            fetchCodes();
        });
    };

    const isBulkSelecting = bulkStage === 'selecting-gl' || bulkStage === 'selecting-gst' || bulkStage === 'selecting-tax';

    return (
        <>
            <div className="tw-flex tw-justify-between tw-mb-6">
                <div className="tw-flex tw-flex-row tw-gap-4 tw-items-end">
                    <div>
                        <h5 className="table__header">{$t('Budget Lines')}</h5>
                        <div className="tw-flex tw-items-center tw-space-x-3 tw-mt-1">
                            <PaginationInfo from={from} to={to} total={total} />
                            <TablePagination lastPage={lastPage} pageChange={(data) => pagination.setPage(+data.selected + 1)} currentPage={page} />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-charcoal-50">
                        <button type="button" onClick={expandAll} disabled={isLoading} className="tw-text-bold tw-underline hover:tw-no-underline">
                            {$t('Expand all')}
                        </button>
                        <button type="button" onClick={collapseAll} disabled={isLoading} className="tw-text-bold tw-underline hover:tw-no-underline">
                            {$t('Collapse all')}
                        </button>
                    </div>
                </div>
                <div className="tw-flex tw-flex-row tw-items-center tw-space-x-2">
                    {deleteStage === 'idle' && bulkStage === 'idle' && (
                        <div className="tw-flex tw-flex-row tw-gap-3">
                            <Button variant="secondary" onClick={handleStartBulkGl} disabled={isLoading}>
                                {$t('Assign GL Code')}
                            </Button>
                            {vatGstEnabled && (
                                <Button variant="secondary" onClick={handleStartBulkGst} disabled={isLoading}>
                                    {$t('Assign GST/VAT')}
                                </Button>
                            )}
                            {taxLines.length > 0 && (
                                <Button variant="secondary" onClick={handleStartBulkTax} disabled={isLoading}>
                                    {$t('Assign Tax Lines')}
                                </Button>
                            )}
                            <Button variant="danger" onClick={handleDeleteMultipleClick} disabled={isLoading || isDeleteLoading}>
                                {$t('Delete Multiple')}
                            </Button>
                            <Button variant="primary" onClick={handleDownload} disabled={isDownloading} loading={isDownloading}>
                                <ExportIcon className="tw-w-3 tw-h-3" />
                                {$t('Download')}
                            </Button>
                        </div>
                    )}

                    {deleteStage === 'choose' && (
                        <>
                            <Button variant="secondary" onClick={handleCancelDelete}>
                                {$t('Cancel Delete Multiple')}
                            </Button>
                            <Button variant="danger" onClick={handleStartDeleteMany}>
                                {$t('Delete Many')}
                            </Button>
                            <Button variant="danger" onClick={handleStartDeleteAll}>
                                {$t('Delete All')}
                            </Button>
                        </>
                    )}

                    {deleteStage === 'selecting' && (
                        <>
                            <Button variant="secondary" onClick={handleCancelDelete} disabled={isDeleteLoading}>
                                {$t('Cancel Delete Multiple')}
                            </Button>
                            <Button variant="danger" onClick={handleSubmitDelete} disabled={selectedIds.length === 0 || isDeleteLoading}>
                                {$t('Submit Delete Multiple')}
                            </Button>
                        </>
                    )}

                    {isBulkSelecting && (
                        <div className="tw-flex tw-flex-row tw-gap-2">
                            <div className="tw-flex tw-flex-row tw-items-center tw-gap-1 tw-mr-2">
                                <span className="tw-text-sm tw-text-gray-600">{bulkSelectedIds.length + bulkSelectedRevLines.length}</span>
                                <span className="tw-text-sm tw-text-gray-600">{$t('selected')}</span>
                            </div>
                            <Button variant="secondary" style={{ width: '14rem' }} onClick={handleSelectAllPageBulk} disabled={isBulkLoading}>
                                {$t('Select All')}
                            </Button>
                            {bulkStage === 'selecting-gl' && (
                                <select
                                    className="select select-input !tw-bg-white !tw-border tw-mr-2"
                                    value={bulkGlCodeId}
                                    onChange={(e) => setBulkGlCodeId(e.target.value)}
                                >
                                    <option value="">{$t('Choose GL Code')}</option>
                                    {Object.values(glCodes).map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.code + ' - ' + item.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {bulkStage === 'selecting-gst' && (
                                <select
                                    className="select select-input !tw-bg-white !tw-border tw-mr-2"
                                    value={bulkGstValue ? '1' : '0'}
                                    onChange={(e) => setBulkGstValue(e.target.value === '1')}
                                >
                                    <option value="1">{$t('GST/VAT Applicable')}</option>
                                    <option value="0">{$t('GST/VAT Not Applicable')}</option>
                                </select>
                            )}
                            {bulkStage === 'selecting-tax' && (
                                <div className="tw-flex tw-flex-row tw-gap-1 tw-items-center">
                                    <Select
                                        isMulti
                                        hideSelectedOptions={false}
                                        options={taxLines.map((tl) => {
                                            const scope = tl.application_scope;
                                            const isAutoApplied = scope && scope !== 'specific_budget_lines';
                                            const disabledReason = isAutoApplied
                                                ? scope === 'all_revenues'
                                                    ? $t('Automatically applied to all revenues. Cannot be selectively assigned.')
                                                    : scope === 'all_expenditure'
                                                      ? $t('Automatically applied to all expenditure. Cannot be selectively assigned.')
                                                      : $t('Automatically applied to all workforce lines. Cannot be selectively assigned.')
                                                : null;
                                            return {
                                                value: tl.id,
                                                label: tl.name,
                                                isDisabled: isAutoApplied,
                                                disabledReason,
                                            };
                                        })}
                                        value={taxLines.filter((tl) => bulkTaxLineIds.includes(tl.id)).map((tl) => ({ value: tl.id, label: tl.name }))}
                                        onChange={(selected) => setBulkTaxLineIds((selected ?? []).filter((o) => !o.isDisabled).map((o) => o.value))}
                                        isOptionDisabled={(option) => !!option.isDisabled}
                                        formatOptionLabel={(option, { context }) => {
                                            const isSelected = bulkTaxLineIds.includes(option.value);
                                            return (
                                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-w-full">
                                                    {option.isDisabled && (
                                                        <Tooltip tooltipType="text" tooltipContent={option.disabledReason} placement="top">
                                                            <span className="tw-flex-shrink-0 tw-text-gray-400 tw-inline-flex tw-items-center">
                                                                <InfoCircleIcon className="tw-w-3.5 tw-h-3.5" style={{ pointerEvents: 'none' }} />
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    <span className={option.isDisabled ? 'tw-text-gray-400' : ''}>{option.label}</span>
                                                    {context === 'menu' && !option.isDisabled && isSelected && (
                                                        <span className="tw-ml-auto tw-text-blue tw-font-bold tw-text-xs tw-flex-shrink-0">&#10003;</span>
                                                    )}
                                                </div>
                                            );
                                        }}
                                        placeholder={$t('Select tax lines...')}
                                        className="react-select-container taxline-select"
                                        classNamePrefix="react-select"
                                        styles={{
                                            container: (base) => ({ ...base, minWidth: '16rem' }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isDisabled
                                                    ? '#f3f4f6'
                                                    : state.isSelected
                                                      ? '#eff6ff'
                                                      : state.isFocused
                                                        ? base.backgroundColor
                                                        : base.backgroundColor,
                                                color: state.isDisabled ? '#9ca3af' : state.isSelected ? '#1d4ed8' : base.color,
                                                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                                                pointerEvents: state.isDisabled ? 'all' : base.pointerEvents,
                                            }),
                                        }}
                                        components={{
                                            MultiValue: (props) => {
                                                if (props.index > 0) return null;
                                                const count = props.getValue().length;
                                                return (
                                                    <>
                                                        <SelectComponents.MultiValue {...props} />
                                                        {count > 1 && (
                                                            <div className="react-select__multi-value tw-ml-0.5 tw-flex-shrink-0">
                                                                <div className="react-select__multi-value__label">+{count - 1}</div>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            },
                                        }}
                                    />
                                    <select
                                        className="select select-input !tw-bg-white !tw-border tw-mr-2"
                                        value={bulkTaxApplicable ? '1' : '0'}
                                        onChange={(e) => setBulkTaxApplicable(e.target.value === '1')}
                                    >
                                        <option value="1">{$t('Include')}</option>
                                        <option value="0">{$t('Exclude')}</option>
                                    </select>
                                </div>
                            )}
                            <Button variant="secondary" onClick={handleCancelBulk} disabled={isBulkLoading}>
                                {$t('Cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={
                                    bulkStage === 'selecting-gl'
                                        ? handleSubmitBulkGl
                                        : bulkStage === 'selecting-gst'
                                          ? handleSubmitBulkGst
                                          : handleSubmitBulkTax
                                }
                                disabled={(bulkSelectedIds.length === 0 && bulkSelectedRevLines.length === 0) || isBulkLoading}
                            >
                                {$t('Apply')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="inbud-table_wrapper tw-overflow-x-auto">
                <table className="inbud-table tw-table-fixed tw-min-w-full">
                    <thead>
                        <tr>
                            {(deleteStage === 'selecting' || isBulkSelecting) && <th className="tw-w-8 tw-sticky tw-left-0 tw-z-10 tw-bg-white" />}
                            <th
                                className={`tw-w-80 tw-sticky tw-z-10 tw-bg-white ${deleteStage === 'selecting' || isBulkSelecting ? 'tw-left-8' : 'tw-left-0'}`}
                            >
                                {$t('Description')}
                            </th>
                            <th className="tw-w-32">{$t('Expenditure type')}</th>
                            <th className="tw-w-28">{$t('Value')}</th>
                            <th className="tw-w-20">{$t('Cash/VIK')}</th>
                            {vatGstEnabled && <th className="tw-w-20">{$t('GST/VAT Applicable')}</th>}
                            {taxLines.map((tl) => (
                                <th key={tl.id} className="tw-w-20 tw-text-xs" title={tl.name}>
                                    {tl.name}
                                </th>
                            ))}
                            <th className="tw-w-64">{$t('GL Code')}</th>
                            <th className="tw-w-20">{$t('Actions')}</th>
                        </tr>
                    </thead>

                    {isLoading ? (
                        <tbody>
                            <tr>
                                <td colSpan={isBulkSelecting || deleteStage === 'selecting' ? colSpan + 1 : colSpan}>Loading...</td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {/* ─── Revenue section (above expenditure) ─── */}
                            {/* ─── Revenue sub-section — independent collapsible ─── */}
                            {revenueLines.revenues.length > 0 && (
                                <>
                                    <tr className="tw-bg-blue-50 tw-cursor-pointer" onClick={() => toggleRevenueSection('revenues')}>
                                        {(deleteStage === 'selecting' || isBulkSelecting) && <td />}
                                        <td colSpan={colSpan} className="tw-font-semibold tw-text-sm tw-pl-3 tw-py-2">
                                            <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                                <span>{$t('Revenue')}</span>
                                                {showRevenues.revenues ? (
                                                    <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" />
                                                ) : (
                                                    <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {showRevenues.revenues &&
                                        revenueLines.revenues.map((rev) => (
                                            <RevenueLineRow
                                                key={`rev-${rev.id}`}
                                                item={rev}
                                                taxLines={taxLines}
                                                vatGstEnabled={vatGstEnabled}
                                                isBulkSelecting={isBulkSelecting}
                                                bulkSelectedRevLines={bulkSelectedRevLines}
                                                onToggleBulkRevLine={handleToggleBulkRevLine}
                                                onChangeGst={onChangeRevGst}
                                                onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                deleteStage={deleteStage}
                                            />
                                        ))}
                                </>
                            )}

                            {/* ─── Sponsors sub-section — independent collapsible ─── */}
                            {revenueLines.sponsors.length > 0 && (
                                <>
                                    <tr className="tw-bg-blue-50 tw-cursor-pointer" onClick={() => toggleRevenueSection('sponsors')}>
                                        {(deleteStage === 'selecting' || isBulkSelecting) && <td />}
                                        <td colSpan={colSpan} className="tw-font-semibold tw-text-sm tw-pl-3 tw-py-2">
                                            <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                                <span>{$t('Sponsors')}</span>
                                                {showRevenues.sponsors ? (
                                                    <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" />
                                                ) : (
                                                    <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {showRevenues.sponsors &&
                                        revenueLines.sponsors.map((sp) => (
                                            <RevenueLineRow
                                                key={`sp-${sp.id}`}
                                                item={sp}
                                                taxLines={taxLines}
                                                vatGstEnabled={vatGstEnabled}
                                                isBulkSelecting={isBulkSelecting}
                                                bulkSelectedRevLines={bulkSelectedRevLines}
                                                onToggleBulkRevLine={handleToggleBulkRevLine}
                                                onChangeGst={onChangeRevGst}
                                                onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                deleteStage={deleteStage}
                                            />
                                        ))}
                                </>
                            )}

                            {/* ─── Ticketing sub-section — independent collapsible ─── */}
                            {revenueLines.ticketing && (
                                <>
                                    <tr className="tw-bg-blue-50 tw-cursor-pointer" onClick={() => toggleRevenueSection('ticketing')}>
                                        {(deleteStage === 'selecting' || isBulkSelecting) && <td />}
                                        <td colSpan={colSpan} className="tw-font-semibold tw-text-sm tw-pl-3 tw-py-2">
                                            <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                                <span>{$t('Ticketing')}</span>
                                                {showRevenues.ticketing ? (
                                                    <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" />
                                                ) : (
                                                    <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {showRevenues.ticketing && (
                                        <RevenueLineRow
                                            item={revenueLines.ticketing}
                                            taxLines={taxLines}
                                            vatGstEnabled={vatGstEnabled}
                                            isBulkSelecting={isBulkSelecting}
                                            bulkSelectedRevLines={bulkSelectedRevLines}
                                            onToggleBulkRevLine={handleToggleBulkRevLine}
                                            onChangeGst={onChangeRevGst}
                                            onChangeTaxLineToggle={onChangeTaxLineToggle}
                                            deleteStage={deleteStage}
                                        />
                                    )}
                                </>
                            )}

                            {/* ─── Expenditure section ─── */}
                            {pagedGroups.map((group, gIdx) => {
                                const groupData = codes[group];

                                const allItems = groupData.items ?? [];
                                const simpleExpenses = allItems.filter((i) => i.entity_type !== 'position');
                                const positionItems = allItems.filter((i) => i.entity_type === 'position');
                                const projects =
                                    groupData.projects && !Array.isArray(groupData.projects) && Object.keys(groupData.projects).length > 0
                                        ? groupData.projects
                                        : {};
                                const templates =
                                    groupData.templates && !Array.isArray(groupData.templates) && Object.keys(groupData.templates).length > 0
                                        ? groupData.templates
                                        : {};
                                const packs =
                                    groupData.packs && !Array.isArray(groupData.packs) && Object.keys(groupData.packs).length > 0 ? groupData.packs : {};

                                return (
                                    <Fragment key={`${group}-group-${gIdx}`}>
                                        {isGrouped && (
                                            <tr onClick={() => toggleGroup(group)} className="tw-cursor-pointer">
                                                {(deleteStage === 'selecting' || isBulkSelecting) && <td />}
                                                <td colSpan={colSpan}>
                                                    <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                                        <div className="tw-font-semibold">{group}</div>
                                                        {showedGroups[group] ? (
                                                            <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" />
                                                        ) : (
                                                            <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        <>
                                            {simpleExpenses.map((se, idx) => {
                                                const isLockedChild = selectedProjectPrefixes.some((prefix) => se.reference_code.startsWith(prefix));
                                                const seGlCode = getGlCode(se.gl_code_id);

                                                return (
                                                    <BudgetLineRow
                                                        key={`${se.id}-${idx}`}
                                                        show={showedGroups[group]}
                                                        showChildren={false}
                                                        hasChildren={false}
                                                        deleteStage={deleteStage}
                                                        bulkStage={bulkStage}
                                                        budgetCode={se}
                                                        selectedIds={selectedIds}
                                                        bulkSelectedIds={bulkSelectedIds}
                                                        isLocked={isLockedChild}
                                                        handleToggleRow={handleToggleRow}
                                                        handleToggleBulkRow={handleToggleBulkRow}
                                                        toggleExpense={() => {}}
                                                        glCode={seGlCode}
                                                        editedCode={se}
                                                        glCodes={glCodes}
                                                        onChangeGlCode={onChangeGlCode}
                                                        onChangeGst={onChangeGst}
                                                        onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                        taxLines={taxLines}
                                                        vatGstEnabled={vatGstEnabled}
                                                        bold={false}
                                                        paddingClass={'tw-pl-3'}
                                                    />
                                                );
                                            })}

                                            {Object.values(packs).map((packData) => {
                                                const pack = packData.pack;
                                                const packChildItems = packData.items ?? [];
                                                const packGlCode = getGlCode(pack.gl_code_id);
                                                const isLockedPack = selectedProjectPrefixes.some((prefix) => pack.reference_code.startsWith(prefix));

                                                return (
                                                    <Fragment key={pack.id}>
                                                        <BudgetLineRow
                                                            show={showedGroups[group]}
                                                            showChildren={!!showedPacks[pack.id]}
                                                            hasChildren={packChildItems.length > 0}
                                                            deleteStage={deleteStage}
                                                            bulkStage={bulkStage}
                                                            budgetCode={pack}
                                                            selectedIds={selectedIds}
                                                            bulkSelectedIds={bulkSelectedIds}
                                                            isLocked={isLockedPack}
                                                            handleToggleRow={handleToggleRow}
                                                            handleToggleBulkRow={handleToggleBulkRow}
                                                            toggleExpense={() => togglePack(pack.id)}
                                                            glCode={packGlCode}
                                                            editedCode={pack}
                                                            glCodes={glCodes}
                                                            onChangeGlCode={onChangeGlCode}
                                                            onChangeGst={onChangeGst}
                                                            onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                            taxLines={taxLines}
                                                            vatGstEnabled={vatGstEnabled}
                                                            bold={true}
                                                            paddingClass={'tw-pl-3'}
                                                        />

                                                        {packChildItems.map((packItem, pkIdx) => {
                                                            return (
                                                                <tr
                                                                    key={`${packItem.id}-${pkIdx}`}
                                                                    className={[
                                                                        '!tw-bg-white',
                                                                        showedGroups[group] && !!showedPacks[pack.id] ? '' : 'tw-hidden',
                                                                    ].join(' ')}
                                                                >
                                                                    {deleteStage === 'selecting' && (
                                                                        <td>
                                                                            {packItem.is_selectable && (
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={selectedIds.includes(packItem.id)}
                                                                                    onChange={() => handleToggleRow(packItem)}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                    {isBulkSelecting && <td />}
                                                                    <td>
                                                                        <span className="tw-pl-4">{packItem.name}</span>
                                                                    </td>
                                                                    <td>{packItem.type}</td>
                                                                    <td className="tw-text-right">{formatFigure(packItem.value, formatAndRoundMoney)}</td>
                                                                    <td>{packItem.cash_vik}</td>
                                                                    {vatGstEnabled && <td>{/* child: GST hidden */}</td>}
                                                                    {taxLines.map((tl) => (
                                                                        <td key={tl.id} />
                                                                    ))}
                                                                    <td>{packGlCode ? packGlCode.code + ' - ' + packGlCode.name : ''}</td>
                                                                    <td>
                                                                        <a href={packItem.link}>
                                                                            <EyeIcon className="tw-w-3 tw-h-3" />
                                                                        </a>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </Fragment>
                                                );
                                            })}

                                            {Object.values(templates).map((tpl, tplIndex) => {
                                                const template = tpl.template;
                                                const templateItems = tpl.items;
                                                const templateGlCode = getGlCode(template.gl_code_id);
                                                const isLockedTemplate = selectedProjectPrefixes.some((prefix) => template.reference_code.startsWith(prefix));

                                                return (
                                                    <Fragment key={`${template.id}-${tplIndex}`}>
                                                        <BudgetLineRow
                                                            show={showedGroups[group]}
                                                            showChildren={displayCodes(group, template.id)}
                                                            hasChildren={true}
                                                            deleteStage={deleteStage}
                                                            bulkStage={bulkStage}
                                                            budgetCode={template}
                                                            selectedIds={selectedIds}
                                                            bulkSelectedIds={bulkSelectedIds}
                                                            isLocked={isLockedTemplate}
                                                            handleToggleRow={handleToggleRow}
                                                            handleToggleBulkRow={handleToggleBulkRow}
                                                            toggleExpense={() => toggleExpense(template.id)}
                                                            glCode={templateGlCode}
                                                            editedCode={template}
                                                            glCodes={glCodes}
                                                            onChangeGlCode={onChangeGlCode}
                                                            onChangeGst={onChangeGst}
                                                            onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                            taxLines={taxLines}
                                                            vatGstEnabled={vatGstEnabled}
                                                            bold={true}
                                                            paddingClass={'tw-pl-3'}
                                                        />

                                                        {templateItems.map((templateItem, tiIndex) => {
                                                            const isLockedTemplateChild = selectedProjectPrefixes.some((prefix) =>
                                                                templateItem.reference_code.startsWith(prefix),
                                                            );

                                                            return (
                                                                <tr
                                                                    key={`${templateItem.id}-${tiIndex}`}
                                                                    className={['!tw-bg-white', displayCodes(group, template.id) ? '' : 'tw-hidden'].join(' ')}
                                                                >
                                                                    {deleteStage === 'selecting' && (
                                                                        <td>
                                                                            {templateItem.is_selectable && (
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={selectedIds.includes(templateItem.id) || isLockedTemplateChild}
                                                                                    disabled={isLockedTemplateChild}
                                                                                    onChange={() => handleToggleRow(templateItem)}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                    {isBulkSelecting && (
                                                                        <td>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={bulkSelectedIds.includes(templateItem.id)}
                                                                                disabled={templateItem.is_pack_child || templateItem.is_template_child}
                                                                                onChange={() =>
                                                                                    handleToggleBulkRow(
                                                                                        templateItem.id,
                                                                                        templateItem.is_pack_child || templateItem.is_template_child,
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    <td>
                                                                        <span className="tw-pl-4">{templateItem.name}</span>
                                                                    </td>
                                                                    <td>{templateItem.type}</td>
                                                                    <td className="tw-text-right">{formatFigure(templateItem.value, formatAndRoundMoney)}</td>
                                                                    <td>{templateItem.cash_vik}</td>
                                                                    {vatGstEnabled && <td>{/* child: GST hidden */}</td>}
                                                                    {taxLines.map((tl) => (
                                                                        <td key={tl.id} />
                                                                    ))}
                                                                    <td>{templateGlCode ? templateGlCode.code + ' - ' + templateGlCode.name : ''}</td>
                                                                    <td>
                                                                        <a href={templateItem.link}>
                                                                            <EyeIcon className="tw-w-3 tw-h-3" />
                                                                        </a>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </Fragment>
                                                );
                                            })}

                                            {Object.values(projects).map((projectItem, piIndex) => {
                                                const projectItems = projectItem.items;
                                                const project = projectItem.project;
                                                const projectTemplates =
                                                    projectItem.templates &&
                                                    !Array.isArray(projectItem.templates) &&
                                                    Object.keys(projectItem.templates).length > 0
                                                        ? projectItem.templates
                                                        : {};
                                                const projectPacks =
                                                    projectItem.packs && !Array.isArray(projectItem.packs) && Object.keys(projectItem.packs).length > 0
                                                        ? projectItem.packs
                                                        : {};

                                                const projectHasChildren =
                                                    (projectItems ?? []).length > 0 ||
                                                    Object.keys(projectTemplates).length > 0 ||
                                                    Object.keys(projectPacks).length > 0;
                                                const projectGlCode = getGlCode(project.gl_code_id);
                                                const isLockedProject = selectedProjectPrefixes.some((prefix) => project.reference_code.startsWith(prefix));

                                                return (
                                                    <Fragment key={`${project.id}-${piIndex}`}>
                                                        <BudgetLineRow
                                                            show={showedGroups[group]}
                                                            showChildren={displayCodes(group, null, project.id)}
                                                            hasChildren={true}
                                                            deleteStage={deleteStage}
                                                            bulkStage={bulkStage}
                                                            budgetCode={project}
                                                            isProject={projectHasChildren}
                                                            selectedIds={selectedIds}
                                                            bulkSelectedIds={bulkSelectedIds}
                                                            isLocked={isLockedProject}
                                                            handleToggleRow={handleToggleRow}
                                                            handleToggleBulkRow={handleToggleBulkRow}
                                                            toggleExpense={() => toggleExpense(null, project.id)}
                                                            glCode={projectGlCode}
                                                            editedCode={project}
                                                            glCodes={glCodes}
                                                            onChangeGlCode={onChangeGlCode}
                                                            onChangeGst={onChangeGst}
                                                            onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                            taxLines={taxLines}
                                                            vatGstEnabled={vatGstEnabled}
                                                            bold={true}
                                                            paddingClass={'tw-pl-3'}
                                                        />

                                                        {projectItems.map((pItem, pIdx) => {
                                                            const isLockedProjectChild = selectedProjectPrefixes.some((prefix) =>
                                                                pItem.reference_code.startsWith(prefix),
                                                            );
                                                            const projectItemGlCode = getGlCode(pItem.gl_code_id);

                                                            return (
                                                                <BudgetLineRow
                                                                    key={`${pItem.id}-${pIdx}`}
                                                                    show={displayCodes(group, null, project.id)}
                                                                    showChildren={false}
                                                                    hasChildren={false}
                                                                    deleteStage={deleteStage}
                                                                    bulkStage={bulkStage}
                                                                    budgetCode={pItem}
                                                                    selectedIds={selectedIds}
                                                                    bulkSelectedIds={bulkSelectedIds}
                                                                    isLocked={isLockedProjectChild}
                                                                    handleToggleRow={handleToggleRow}
                                                                    handleToggleBulkRow={handleToggleBulkRow}
                                                                    toggleExpense={() => {}}
                                                                    glCode={projectItemGlCode}
                                                                    editedCode={pItem}
                                                                    glCodes={glCodes}
                                                                    onChangeGlCode={onChangeGlCode}
                                                                    onChangeGst={onChangeGst}
                                                                    onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                                    taxLines={taxLines}
                                                                    vatGstEnabled={vatGstEnabled}
                                                                    bold={false}
                                                                    paddingClass={'tw-pl-4'}
                                                                />
                                                            );
                                                        })}

                                                        {Object.values(projectTemplates).map((pTpl, pTplIndex) => {
                                                            const pTemplate = pTpl.template;
                                                            const pTemplateItems = pTpl.items;
                                                            const pTemplateGlCode = getGlCode(pTemplate.gl_code_id);
                                                            const isLockedProjectTemplate = selectedProjectPrefixes.some((prefix) =>
                                                                pTemplate.reference_code.startsWith(prefix),
                                                            );

                                                            return (
                                                                <Fragment key={`${pTemplate.id}-${pTplIndex}`}>
                                                                    <BudgetLineRow
                                                                        show={displayCodes(group, null, project.id)}
                                                                        showChildren={displayCodes(group, null, project.id, pTemplate.id)}
                                                                        hasChildren={true}
                                                                        deleteStage={deleteStage}
                                                                        bulkStage={bulkStage}
                                                                        budgetCode={pTemplate}
                                                                        selectedIds={selectedIds}
                                                                        bulkSelectedIds={bulkSelectedIds}
                                                                        isLocked={isLockedProjectTemplate}
                                                                        handleToggleRow={handleToggleRow}
                                                                        handleToggleBulkRow={handleToggleBulkRow}
                                                                        toggleExpense={() => toggleExpense(null, project.id, pTemplate.id)}
                                                                        glCode={pTemplateGlCode}
                                                                        editedCode={pTemplate}
                                                                        glCodes={glCodes}
                                                                        onChangeGlCode={onChangeGlCode}
                                                                        onChangeGst={onChangeGst}
                                                                        onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                                        taxLines={taxLines}
                                                                        vatGstEnabled={vatGstEnabled}
                                                                        bold={true}
                                                                        paddingClass={'tw-pl-4'}
                                                                    />

                                                                    {pTemplateItems.map((pTemplateItem, pTiIndex) => {
                                                                        const isLockedProjectTemplateChild = selectedProjectPrefixes.some((prefix) =>
                                                                            pTemplateItem.reference_code.startsWith(prefix),
                                                                        );

                                                                        return (
                                                                            <tr
                                                                                key={`${pTemplateItem.id}-${pTiIndex}`}
                                                                                className={[
                                                                                    '!tw-bg-white',
                                                                                    displayCodes(group, null, project.id, pTemplate.id) ? '' : 'tw-hidden',
                                                                                ].join(' ')}
                                                                            >
                                                                                {deleteStage === 'selecting' && (
                                                                                    <td>
                                                                                        {pTemplateItem.is_selectable && (
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={
                                                                                                    selectedIds.includes(pTemplateItem.id) ||
                                                                                                    isLockedProjectTemplateChild
                                                                                                }
                                                                                                disabled={isLockedProjectTemplateChild}
                                                                                                onChange={() => handleToggleRow(pTemplateItem)}
                                                                                            />
                                                                                        )}
                                                                                    </td>
                                                                                )}
                                                                                {isBulkSelecting && (
                                                                                    <td>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={bulkSelectedIds.includes(pTemplateItem.id)}
                                                                                            disabled={
                                                                                                pTemplateItem.is_pack_child || pTemplateItem.is_template_child
                                                                                            }
                                                                                            onChange={() =>
                                                                                                handleToggleBulkRow(
                                                                                                    pTemplateItem.id,
                                                                                                    pTemplateItem.is_pack_child ||
                                                                                                        pTemplateItem.is_template_child,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                                <td>
                                                                                    <span className="tw-pl-5">{pTemplateItem.name}</span>
                                                                                </td>
                                                                                <td>{pTemplateItem.type}</td>
                                                                                <td className="tw-text-right">
                                                                                    {formatFigure(pTemplateItem.value, formatAndRoundMoney)}
                                                                                </td>
                                                                                <td>{pTemplateItem.cash_vik}</td>
                                                                                {vatGstEnabled && <td>{/* child: GST hidden */}</td>}
                                                                                {taxLines.map((tl) => (
                                                                                    <td key={tl.id} />
                                                                                ))}
                                                                                <td>
                                                                                    {pTemplateGlCode ? pTemplateGlCode.code + ' - ' + pTemplateGlCode.name : ''}
                                                                                </td>
                                                                                <td>
                                                                                    <a href={pTemplateItem.link}>
                                                                                        <EyeIcon className="tw-w-3 tw-h-3" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </Fragment>
                                                            );
                                                        })}

                                                        {Object.values(projectPacks).map((projPackData) => {
                                                            const projPack = projPackData.pack;
                                                            const projPackChildItems = projPackData.items ?? [];
                                                            const projPackGlCode = getGlCode(projPack.gl_code_id);

                                                            return (
                                                                <Fragment key={projPack.id}>
                                                                    <BudgetLineRow
                                                                        show={displayCodes(group, null, project.id)}
                                                                        showChildren={!!showedPacks[projPack.id]}
                                                                        hasChildren={projPackChildItems.length > 0}
                                                                        deleteStage={deleteStage}
                                                                        bulkStage={bulkStage}
                                                                        budgetCode={projPack}
                                                                        selectedIds={selectedIds}
                                                                        bulkSelectedIds={bulkSelectedIds}
                                                                        isLocked={false}
                                                                        handleToggleRow={handleToggleRow}
                                                                        handleToggleBulkRow={handleToggleBulkRow}
                                                                        toggleExpense={() => togglePack(projPack.id)}
                                                                        glCode={projPackGlCode}
                                                                        editedCode={projPack}
                                                                        glCodes={glCodes}
                                                                        onChangeGlCode={onChangeGlCode}
                                                                        onChangeGst={onChangeGst}
                                                                        onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                                        taxLines={taxLines}
                                                                        vatGstEnabled={vatGstEnabled}
                                                                        bold={true}
                                                                        paddingClass={'tw-pl-4'}
                                                                    />

                                                                    {projPackChildItems.map((projPackItem, projPkIdx) => {
                                                                        return (
                                                                            <tr
                                                                                key={`${projPackItem.id}-${projPkIdx}`}
                                                                                className={[
                                                                                    '!tw-bg-white',
                                                                                    displayCodes(group, null, project.id) && !!showedPacks[projPack.id]
                                                                                        ? ''
                                                                                        : 'tw-hidden',
                                                                                ].join(' ')}
                                                                            >
                                                                                {deleteStage === 'selecting' && (
                                                                                    <td>
                                                                                        {projPackItem.is_selectable && (
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={selectedIds.includes(projPackItem.id)}
                                                                                                onChange={() => handleToggleRow(projPackItem)}
                                                                                            />
                                                                                        )}
                                                                                    </td>
                                                                                )}
                                                                                {isBulkSelecting && <td />}
                                                                                <td>
                                                                                    <span className="tw-pl-5">{projPackItem.name}</span>
                                                                                </td>
                                                                                <td>{projPackItem.type}</td>
                                                                                <td className="tw-text-right">
                                                                                    {formatFigure(projPackItem.value, formatAndRoundMoney)}
                                                                                </td>
                                                                                <td>{projPackItem.cash_vik}</td>
                                                                                {vatGstEnabled && <td>{/* child: GST hidden */}</td>}
                                                                                {taxLines.map((tl) => (
                                                                                    <td key={tl.id} />
                                                                                ))}
                                                                                <td>
                                                                                    {projPackGlCode ? projPackGlCode.code + ' - ' + projPackGlCode.name : ''}
                                                                                </td>
                                                                                <td>
                                                                                    <a href={projPackItem.link}>
                                                                                        <EyeIcon className="tw-w-3 tw-h-3" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </Fragment>
                                                            );
                                                        })}
                                                    </Fragment>
                                                );
                                            })}

                                            {positionItems.length > 0 && (
                                                <>
                                                    <WorkforceHeaderRow
                                                        show={showedGroups[group]}
                                                        showChildren={!!showedWorkforce[group]}
                                                        positions={positionItems}
                                                        glCodes={glCodes}
                                                        onToggle={() => toggleWorkforce(group)}
                                                        onGlChange={(glCodeId) => handleWorkforceGlChange(group, glCodeId)}
                                                        onGstChange={(checked) => handleWorkforceGstChange(group, checked)}
                                                        deleteStage={deleteStage}
                                                        bulkStage={bulkStage}
                                                        taxLines={taxLines}
                                                        vatGstEnabled={vatGstEnabled}
                                                    />
                                                    {positionItems.map((pos, posIdx) => {
                                                        const posGlCode = getGlCode(pos.gl_code_id);
                                                        return (
                                                            <BudgetLineRow
                                                                key={`${pos.id}-${posIdx}`}
                                                                show={showedGroups[group] && !!showedWorkforce[group]}
                                                                showChildren={false}
                                                                hasChildren={false}
                                                                deleteStage={deleteStage}
                                                                bulkStage={bulkStage}
                                                                budgetCode={pos}
                                                                selectedIds={selectedIds}
                                                                bulkSelectedIds={bulkSelectedIds}
                                                                isLocked={false}
                                                                handleToggleRow={handleToggleRow}
                                                                handleToggleBulkRow={handleToggleBulkRow}
                                                                toggleExpense={() => {}}
                                                                glCode={posGlCode}
                                                                editedCode={pos}
                                                                glCodes={glCodes}
                                                                onChangeGlCode={onChangeGlCode}
                                                                onChangeGst={onChangeGst}
                                                                onChangeTaxLineToggle={onChangeTaxLineToggle}
                                                                taxLines={taxLines}
                                                                vatGstEnabled={vatGstEnabled}
                                                                bold={false}
                                                                paddingClass={'tw-pl-5'}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </>
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    )}
                </table>
            </div>

            {!isLoading && lastPage > 1 && (
                <div className="tw-flex tw-items-center tw-justify-between tw-mt-4">
                    <PaginationInfo from={from} to={to} total={total} />
                    <TablePagination lastPage={lastPage} pageChange={(data) => pagination.setPage(+data.selected + 1)} currentPage={page} />
                </div>
            )}
        </>
    );
}

function getTaxLineDisabledTooltip(scope, name) {
    if (scope === 'all_revenues') return $t("Disabled — '{{name}}' application scope is set to 'All Revenues' in Cashflow Settings", { name });
    if (scope === 'all_expenditure') return $t("Disabled — '{{name}}' application scope is set to 'All Expenditure' in Cashflow Settings", { name });
    if (scope === 'workforce_lines_only') return $t("Disabled — '{{name}}' application scope is set to 'Workforce Lines Only' in Cashflow Settings", { name });
    return $t("Disabled — '{{name}}' is applied automatically based on its scope in Cashflow Settings", { name });
}

// Revenue/Sponsor/Ticketing line row (no GL code, no delete selection)
const RevenueLineRow = ({
    item,
    taxLines,
    vatGstEnabled,
    isBulkSelecting,
    bulkSelectedRevLines,
    onToggleBulkRevLine,
    onChangeGst,
    onChangeTaxLineToggle,
    deleteStage,
}) => {
    const isSelected = bulkSelectedRevLines.some((r) => r.id === item.id && r.type === item.entity_type);

    return (
        <tr className="!tw-bg-white">
            {deleteStage === 'selecting' && <td />}
            {isBulkSelecting && (
                <td>
                    <input type="checkbox" checked={isSelected} onChange={() => onToggleBulkRevLine(item.id, item.entity_type)} />
                </td>
            )}
            <td className={`tw-pl-4 tw-sticky tw-z-10 tw-bg-white ${deleteStage === 'selecting' || isBulkSelecting ? 'tw-left-8' : 'tw-left-0'}`}>
                {item.name}
            </td>
            <td />
            <td className="tw-text-right">{formatFigure(item.value, formatAndRoundMoney)}</td>
            <td>{item.cash_vik}</td>
            {vatGstEnabled && (
                <td>
                    <ToggleInput
                        checked={!!item.gst_vat_applicable}
                        onChange={(e) => onChangeGst(e.target.checked, item)}
                        label={item.gst_vat_applicable ? $t('Yes') : $t('No')}
                    />
                </td>
            )}
            {taxLines.map((tl) => {
                const applicable = isTaxLineApplicable(tl, item.id, item.entity_type);
                const isEditable = tl.application_scope === 'specific_budget_lines';
                return (
                    <td key={tl.id}>
                        <ToggleInput
                            checked={applicable}
                            onChange={isEditable ? (e) => onChangeTaxLineToggle(tl.id, item.id, item.entity_type, e.target.checked) : undefined}
                            disabled={!isEditable}
                            label={applicable ? $t('Yes') : $t('No')}
                            tooltip={!isEditable ? getTaxLineDisabledTooltip(tl.application_scope, tl.name) : undefined}
                        />
                    </td>
                );
            })}
            <td />
            <td>
                {item.link && (
                    <div className="tw-flex tw-items-center tw-justify-start">
                        <a href={item.link}>
                            <EyeIcon className="tw-w-3 tw-h-3" />
                        </a>
                    </div>
                )}
            </td>
        </tr>
    );
};

// Workforce positions carry only their own value; budget lines also expose the inherited one.
const isGstEffective = (code) => code.gst_vat_effective ?? !!code.gst_vat_applicable;

const BudgetLineRow = (props) => {
    const isChild = props.budgetCode.is_pack_child || props.budgetCode.is_template_child;
    const isBulkSelecting = props.bulkStage === 'selecting-gl' || props.bulkStage === 'selecting-gst' || props.bulkStage === 'selecting-tax';
    const vatGstEnabled = props.vatGstEnabled !== false;
    const gstEffective = isGstEffective(props.budgetCode);

    return (
        <tr className={['!tw-bg-white', props.show ? '' : 'tw-hidden'].join(' ')}>
            {props.deleteStage === 'selecting' && (
                <td>
                    {props.budgetCode.is_selectable && (
                        <input
                            type="checkbox"
                            checked={props.selectedIds.includes(props.budgetCode.id) || props.isLocked}
                            disabled={props.isLocked}
                            onChange={() => props.handleToggleRow(props.budgetCode)}
                        />
                    )}
                </td>
            )}
            {isBulkSelecting && (
                <td>
                    <input
                        type="checkbox"
                        checked={props.bulkSelectedIds.includes(props.budgetCode.id)}
                        disabled={isChild || (props.bulkStage === 'selecting-gl' && !props.budgetCode.is_selectable)}
                        onChange={() => props.handleToggleBulkRow(props.budgetCode.id, isChild)}
                    />
                </td>
            )}
            <td
                onClick={props.toggleExpense}
                className={`tw-cursor-pointer tw-sticky tw-z-10 tw-bg-white ${props.deleteStage === 'selecting' || isBulkSelecting ? 'tw-left-8' : 'tw-left-0'}`}
            >
                <div className={`tw-flex tw-items-center tw-gap-x-1.5 ${props.paddingClass}`}>
                    <div className={props.bold ? 'tw-font-semibold' : ''}>{props.budgetCode.name}</div>
                    {props.hasChildren && (
                        <>{props.showChildren ? <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" /> : <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />}</>
                    )}
                </div>
            </td>
            <td>{props.budgetCode.type}</td>
            <td className="tw-text-right">{formatFigure(props.budgetCode.value, formatAndRoundMoney)}</td>
            <td>{props.budgetCode.cash_vik}</td>
            {vatGstEnabled && (
                <td>
                    {!isChild && (
                        <div className="tw-flex tw-items-center tw-gap-x-1.5">
                            <ToggleInput
                                checked={gstEffective}
                                onChange={(e) => props.onChangeGst(e.target.checked, props.budgetCode)}
                                label={gstEffective ? $t('Yes') : $t('No')}
                                tooltip={
                                    props.isProject
                                        ? $t("Applies to this project's unallocated balance. Child lines inherit unless set individually.")
                                        : undefined
                                }
                            />
                            {props.budgetCode.gst_vat_inherited && <span className="tw-text-gray-400 tw-mb-2">{$t('Inherited')}</span>}
                        </div>
                    )}
                </td>
            )}
            {(props.taxLines ?? []).map((tl) => {
                if (isChild) return <td key={tl.id} />;
                const entityType = props.budgetCode.entity_type ?? 'expense';
                const applicable = isTaxLineApplicable(tl, props.budgetCode.id, entityType);
                const isEditable = tl.application_scope === 'specific_budget_lines';
                return (
                    <td key={tl.id}>
                        <ToggleInput
                            checked={applicable}
                            onChange={isEditable ? (e) => props.onChangeTaxLineToggle(tl.id, props.budgetCode.id, entityType, e.target.checked) : undefined}
                            disabled={!isEditable}
                            label={applicable ? $t('Yes') : $t('No')}
                            tooltip={!isEditable ? getTaxLineDisabledTooltip(tl.application_scope, tl.name) : undefined}
                        />
                    </td>
                );
            })}
            <td>
                {!!props.editedCode && props.editedCode.is_gl_code_editable && (
                    <select
                        className="select select-input !tw-bg-white !tw-border"
                        value={props.editedCode.gl_code_id ?? ''}
                        onChange={(e) => props.onChangeGlCode(e, props.editedCode)}
                    >
                        <option value={''}>{$t('Choose Gl code')}</option>
                        {Object.values(props.glCodes).map((item) => {
                            return (
                                <option key={item.id} value={item.id}>
                                    {item.code + ' - ' + item.name}
                                </option>
                            );
                        })}
                    </select>
                )}
                {(!props.editedCode || !props.editedCode.is_gl_code_editable) && <>{props.glCode ? props.glCode.code + ' - ' + props.glCode.name : ''}</>}
            </td>
            <td>
                <div className="tw-flex tw-items-center tw-justify-start">
                    <div>
                        <a href={props.budgetCode.link}>
                            <EyeIcon className="tw-w-3 tw-h-3" />
                        </a>
                    </div>
                </div>
            </td>
        </tr>
    );
};

const WorkforceHeaderRow = ({ show, showChildren, positions, glCodes, onToggle, onGlChange, onGstChange, deleteStage, bulkStage, taxLines, vatGstEnabled }) => {
    if (!show) return null;

    const isBulkSelecting = bulkStage === 'selecting-gl' || bulkStage === 'selecting-gst' || bulkStage === 'selecting-tax';

    const glCodeIds = positions.map((p) => p.gl_code_id ?? '');
    const allSameGl = glCodeIds.length > 0 && glCodeIds.every((id) => id === glCodeIds[0]);
    const commonGlCodeId = allSameGl ? (glCodeIds[0] ?? '') : '';

    const vatChecked = positions.length > 0 && positions.every((p) => !!p.gst_vat_applicable);
    const vatIndeterminate = positions.some((p) => !!p.gst_vat_applicable) && !vatChecked;
    const hasEditableGlCode = positions.some((p) => p.is_gl_code_editable);

    return (
        <tr className="!tw-bg-gray-50">
            {deleteStage === 'selecting' && <td />}
            {isBulkSelecting && <td />}
            <td onClick={onToggle} className="tw-cursor-pointer">
                <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-pl-3">
                    <span className="tw-font-semibold tw-text-sm">{$t('Workforce')}</span>
                    {showChildren ? <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" /> : <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />}
                </div>
            </td>
            <td />
            <td />
            <td />
            {vatGstEnabled !== false && (
                <td>
                    <ToggleInput
                        checked={vatChecked}
                        indeterminate={vatIndeterminate}
                        onChange={(e) => onGstChange(e.target.checked)}
                        label={vatIndeterminate ? $t('Mixed') : vatChecked ? $t('Yes') : $t('No')}
                    />
                </td>
            )}
            {(taxLines ?? []).map((tl) => (
                <td key={tl.id} />
            ))}
            <td>
                {hasEditableGlCode && (
                    <select className="select select-input !tw-bg-white !tw-border" value={commonGlCodeId} onChange={(e) => onGlChange(e.target.value)}>
                        <option value="">{$t('Choose Gl code')}</option>
                        {Object.values(glCodes).map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.code + ' - ' + item.name}
                            </option>
                        ))}
                    </select>
                )}
            </td>
            <td />
        </tr>
    );
};

if (document.getElementById('budgetCodesIndex')) {
    ReactDOM.render(
        <App>
            <BudgetCodesIndex />
        </App>,
        document.getElementById('budgetCodesIndex'),
    );
}
